// Portfolio-Store. V1-Pendant: localStorage key `eve_portfolio_v1`.
// V3-Refactor: Zustand mit persist-Middleware. Auto-Migration: V1-altes
// $10k-Portfolio → $500k beim ersten Mount.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  applyTradeResult,
  createDefaultPortfolio,
  formatPortfolioForPrompt,
  getRecommendedCapital,
  migratePortfolio,
  resetPortfolioState,
  shouldMigrate,
} from '@/lib/portfolio';
import type { PortfolioState } from '@/types/portfolio';

export const PORTFOLIO_STORE_KEY = 'eve_portfolio_v3';

export interface PortfolioStoreState {
  portfolio: PortfolioState;
}

export interface PortfolioStoreActions {
  /** Idempotenter Initialisierer — wird beim Hydrate aufgerufen. Auto-migriert V1-Altdaten. */
  ensureInitialized: () => PortfolioState;
  applyTrade: (pnlAbsolute: number) => PortfolioState;
  reset: () => PortfolioState;
  recommendedCapital: () => number;
  promptBlock: () => string;
  /** Wenn vorhanden — direkt setzen (für Tests + D1-Sync). */
  replace: (next: PortfolioState) => void;
}

export type PortfolioStore = PortfolioStoreState & PortfolioStoreActions;

const initialPortfolio = createDefaultPortfolio();

export const usePortfolioStore = create<PortfolioStore>()(
  persist(
    (set, get) => ({
      portfolio: initialPortfolio,

      ensureInitialized: () => {
        const current = get().portfolio;
        if (shouldMigrate(current)) {
          const migrated = migratePortfolio(current);
          set({ portfolio: migrated });
          return migrated;
        }
        return current;
      },

      applyTrade: (pnlAbsolute) => {
        const current = get().portfolio;
        const next = applyTradeResult(current, pnlAbsolute);
        set({ portfolio: next });
        return next;
      },

      reset: () => {
        const next = resetPortfolioState();
        set({ portfolio: next });
        return next;
      },

      recommendedCapital: () => getRecommendedCapital(get().portfolio),
      promptBlock: () => formatPortfolioForPrompt(get().portfolio),
      replace: (next) => set({ portfolio: next }),
    }),
    {
      name: PORTFOLIO_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ portfolio: state.portfolio }),
      version: 1,
    },
  ),
);

export const selectPortfolio = (s: PortfolioStoreState): PortfolioState => s.portfolio;
